import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spinner, Form } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { buscarClientes } from '../../services/clienteService';
import 'react-toastify/dist/ReactToastify.css';
import {
    crearLote,
    listarLotes,
    obtenerLote,
    listarFilas,
    eliminarLote,
    editarFila,
    ignorarFila,
    aplicarFila,
    corregirEnMasa,
    confirmarLote,
} from '../../services/importacionService';
import '../../style/pages/HomePage.css';
import '../../style/pages/ImportacionLote.css';
import { apellidoNombre } from '../../utils/nombrePersona';
import { coincideBusqueda } from '../../utils/textoComparable';

// Cómo se pinta cada estado de fila. El color es el que decide de un vistazo qué hay que mirar.
const META_ESTADO = {
    OK: { etiqueta: 'Listas', clase: 'lote-chip-ok', icono: 'bi-check-circle' },
    ADVERTENCIA: { etiqueta: 'Con avisos', clase: 'lote-chip-adv', icono: 'bi-exclamation-triangle' },
    ERROR: { etiqueta: 'Con errores', clase: 'lote-chip-err', icono: 'bi-x-octagon' },
    IGNORADA: { etiqueta: 'Salteadas', clase: 'lote-chip-ign', icono: 'bi-slash-circle' },
    APLICADA: { etiqueta: 'Aplicadas', clase: 'lote-chip-apl', icono: 'bi-check2-all' },
};

// Los campos que se pueden corregir a mano, con la ruta que entiende el backend. Las anidadas
// llevan punto: el backend hace el merge por nivel para no pisar el resto del turno.
const CAMPOS_EDITABLES = [
    { ruta: 'cuil', etiqueta: 'CUIL' },
    { ruta: 'apellido', etiqueta: 'Apellido' },
    { ruta: 'nombre', etiqueta: 'Nombre' },
    { ruta: 'telefonoPropio', etiqueta: 'Teléfono' },
    { ruta: 'turno.fecha', etiqueta: 'Fecha del turno (AAAA-MM-DD)' },
    { ruta: 'turno.horaAnses', etiqueta: 'Hora en ANSES (HH:mm)' },
    { ruta: 'turno.udai', etiqueta: 'UDAI' },
    { ruta: 'turno.tipoTramite', etiqueta: 'Tipo de trámite' },
    { ruta: 'turno.subtipo', etiqueta: 'Subtipo / beneficio' },
    {
        ruta: 'turno.accionTramite',
        etiqueta: 'Qué hacer con la carpeta',
        opciones: [
            { valor: 'CREAR', etiqueta: 'Abrir una carpeta nueva' },
            { valor: 'EXISTENTE', etiqueta: 'Usar un trámite que ya existe' },
            { valor: 'SOLO_TURNO', etiqueta: 'Sin carpeta: cargar solo el turno' },
        ],
    },
    // Sin opciones fijas: las arma la propia fila con los trámites del cliente.
    { ruta: 'turno.idTramiteExistente', etiqueta: 'Trámite existente al que se cuelga' },
    // Las claves las descarta el parser cuando el texto no parece una clave. Se pueden escribir
    // acá; el modal viene precargado con lo que decía la planilla.
    { ruta: 'claveSs', etiqueta: 'Clave ANSES', indiceCrudo: 9 },
    { ruta: 'claveAfip', etiqueta: 'Clave AFIP', indiceCrudo: 10 },
    { ruta: 'claveCidi', etiqueta: 'Clave CIDI', indiceCrudo: 11 },
    { ruta: 'observacion', etiqueta: 'Observación' },
    // Contra qué cliente del estudio se vincula la referencia. Es una búsqueda, no texto libre.
    { ruta: 'referencia.cuilClienteReferente', etiqueta: 'Cliente que lo refirió', buscaCliente: true },
];

// El subtipo no es texto libre: cada trámite tiene su lista cerrada. En Reajuste ni siquiera es
// un subtipo, es de qué haber se reclama.
const SUBTIPOS_POR_TRAMITE = {
    JUBILACION: [
        { valor: 'ORDINARIA', etiqueta: 'Ordinaria' },
        { valor: 'REGIMEN_DIFERENCIAL', etiqueta: 'Régimen diferencial' },
        { valor: 'CASAS_PARTICULARES', etiqueta: 'Casas particulares (servicio doméstico)' },
    ],
    RECONOCIMIENTO: [
        { valor: 'COMUN', etiqueta: 'Común' },
        { valor: 'SERVICIO_DOMESTICO', etiqueta: 'Servicio doméstico' },
    ],
    REAJUSTE: [
        { valor: 'JUBILACION', etiqueta: 'Sobre un haber de jubilación' },
        { valor: 'PENSION', etiqueta: 'Sobre un haber de pensión' },
    ],
};

// Los del turnero son otros: esa planilla no trae CUIL ni carpeta, y en cambio trae la hora de
// citación real y el turno de escribanía, que son justamente los datos que solo viven ahí.
const CAMPOS_EDITABLES_TURNERO = [
    { ruta: 'apellido', etiqueta: 'Apellido' },
    { ruta: 'nombre', etiqueta: 'Nombre' },
    { ruta: 'telefono', etiqueta: 'Teléfono' },
    { ruta: 'fecha', etiqueta: 'Fecha (AAAA-MM-DD)' },
    { ruta: 'horario', etiqueta: 'Hora de citación (HH:mm)' },
    { ruta: 'tipoTurno', etiqueta: 'Tipo de turno' },
    { ruta: 'horaEscribania', etiqueta: 'Hora de escribanía (HH:mm)' },
    { ruta: 'idTurnoAnses', etiqueta: 'Turno ANSES al que corrige' },
];

// La solapa LIQUIDAR: el cliente (mismas columnas que TURNOS ANSES, en otro orden) y la carpeta
// que espera su mes. Los índices de las claves son los de la solapa (F, G, H).
const TIPOS_TRAMITE_LIQUIDAR = [
    { valor: 'JUBILACION', etiqueta: 'Jubilación' },
    { valor: 'PUAM', etiqueta: 'PUAM' },
    { valor: 'PENSION', etiqueta: 'Pensión' },
    { valor: 'PLAN_PAGO', etiqueta: 'Plan de pago' },
    { valor: 'RECONOCIMIENTO', etiqueta: 'Reconocimiento de servicios' },
    { valor: 'PNC', etiqueta: 'PNC' },
    { valor: 'RTI', etiqueta: 'RTI' },
    { valor: 'MINUSVALIA', etiqueta: 'Minusvalía (no aparece en A liquidar)' },
    { valor: 'REAJUSTE', etiqueta: 'Reajuste (no aparece en A liquidar)' },
    { valor: 'GENERICO', etiqueta: 'Genérico' },
];

const CAMPOS_EDITABLES_LIQUIDAR = [
    { ruta: 'cuil', etiqueta: 'CUIL' },
    { ruta: 'apellido', etiqueta: 'Apellido' },
    { ruta: 'nombre', etiqueta: 'Nombre' },
    { ruta: 'telefonoPropio', etiqueta: 'Teléfono' },
    { ruta: 'tipoTramite', etiqueta: 'Tipo de trámite', opciones: TIPOS_TRAMITE_LIQUIDAR },
    { ruta: 'subtipo', etiqueta: 'Subtipo / beneficio' },
    { ruta: 'fechaLiquidar', etiqueta: 'Fecha de liquidar', tipo: 'date' },
    { ruta: 'carpeta', etiqueta: 'Carpeta física' },
    {
        ruta: 'accionTramite',
        etiqueta: 'Qué hacer con la carpeta',
        opciones: [
            { valor: 'CREAR', etiqueta: 'Abrir un trámite nuevo' },
            { valor: 'EXISTENTE', etiqueta: 'Completar un trámite que ya existe' },
        ],
    },
    { ruta: 'idTramiteExistente', etiqueta: 'Trámite existente que se completa' },
    { ruta: 'claveSs', etiqueta: 'Clave ANSES', indiceCrudo: 5 },
    { ruta: 'claveAfip', etiqueta: 'Clave AFIP', indiceCrudo: 6 },
    { ruta: 'claveCidi', etiqueta: 'Clave CIDI', indiceCrudo: 7 },
    { ruta: 'observacion', etiqueta: 'Observación del cliente' },
    { ruta: 'referencia.cuilClienteReferente', etiqueta: 'Cliente que lo refirió', buscaCliente: true },
];

// Títulos de las columnas de la solapa, para "Ver planilla" (la última es el bloque).
const COLUMNAS_LIQUIDAR = ['Nombre', 'Estado del trámite', 'CUIL', 'Referencia', 'Teléfono',
    'Clave SS', 'AFIP', 'Clave CiDi', 'Estado del control', 'Bloque'];

// PARA APROBAR: lo mismo que LIQUIDAR más lo de ANSES (expediente, fecha de ingreso) y los datos
// propios de la pensión. Las claves están en otras columnas (N, O, P).
const CAMPOS_EDITABLES_PARA_APROBAR = [
    { ruta: 'cuil', etiqueta: 'CUIL' },
    { ruta: 'apellido', etiqueta: 'Apellido' },
    { ruta: 'nombre', etiqueta: 'Nombre' },
    { ruta: 'telefonoPropio', etiqueta: 'Teléfono' },
    {
        ruta: 'tipoTramite', etiqueta: 'Tipo de trámite',
        opciones: [
            { valor: 'JUBILACION', etiqueta: 'Jubilación' },
            { valor: 'PENSION', etiqueta: 'Pensión' },
            { valor: 'RECONOCIMIENTO', etiqueta: 'Reconocimiento de servicios' },
            { valor: 'PUAM', etiqueta: 'PUAM' },
            { valor: 'PNC', etiqueta: 'PNC' },
            { valor: 'AMPARO_PNC', etiqueta: 'Amparo PNC' },
            { valor: 'REAJUSTE', etiqueta: 'Reajuste (Exp. 357: queda en Reclamo administrativo)' },
            { valor: 'RTI', etiqueta: 'RTI (Caja Provincia)' },
            { valor: 'GENERICO', etiqueta: 'Genérico (con el título de abajo)' },
        ],
    },
    { ruta: 'tituloGenerico', etiqueta: 'Título del genérico' },
    {
        ruta: 'organismo', etiqueta: 'Organismo',
        opciones: [
            { valor: '', etiqueta: 'ANSES' },
            { valor: 'PROVINCIA', etiqueta: 'Caja de la Provincia (sin robot, sección propia)' },
        ],
    },
    { ruta: 'subtipo', etiqueta: 'Subtipo / beneficio' },
    {
        ruta: 'tipoPension', etiqueta: 'Tipo de pensión',
        opciones: [{ valor: 'DIRECTA', etiqueta: 'Directa' }, { valor: 'DERIVADA', etiqueta: 'Derivada' }],
    },
    {
        ruta: 'vinculoBeneficiario', etiqueta: 'Vínculo del beneficiario',
        opciones: [
            { valor: 'CONYUGE', etiqueta: 'Cónyuge' },
            { valor: 'CONVIVIENTE', etiqueta: 'Conviviente' },
            { valor: 'HIJO_MENOR', etiqueta: 'Hijo menor' },
            { valor: 'HIJO_CON_DISCAPACIDAD', etiqueta: 'Hijo con discapacidad' },
        ],
    },
    {
        ruta: 'accionTramite', etiqueta: 'Qué hacer con la carpeta',
        opciones: [
            { valor: 'CREAR', etiqueta: 'Abrir un trámite nuevo' },
            { valor: 'EXISTENTE', etiqueta: 'Completar un trámite que ya existe' },
        ],
    },
    { ruta: 'idTramiteExistente', etiqueta: 'Trámite existente que se completa' },
    { ruta: 'nroExpediente', etiqueta: 'N° de expediente' },
    { ruta: 'fechaIngresoAnses', etiqueta: 'Fecha de ingreso a ANSES', tipo: 'date' },
    { ruta: 'carpeta', etiqueta: 'Carpeta física' },
    { ruta: 'claveSs', etiqueta: 'Clave ANSES', indiceCrudo: 13 },
    { ruta: 'claveAfip', etiqueta: 'Clave AFIP', indiceCrudo: 14 },
    { ruta: 'claveCidi', etiqueta: 'Clave CIDI', indiceCrudo: 15 },
    { ruta: 'observacion', etiqueta: 'Observación del cliente' },
    { ruta: 'referencia.cuilClienteReferente', etiqueta: 'Cliente que lo refirió', buscaCliente: true },
];

// FAVORABLES: lo mismo que PARA APROBAR, más lo del cobro. La clave de ANSES es la columna J (CSS).
const CAMPOS_EDITABLES_FAVORABLES = [
    ...CAMPOS_EDITABLES_PARA_APROBAR.filter((c) => !['organismo', 'fechaIngresoAnses', 'carpeta', 'claveAfip', 'claveCidi', 'claveSs'].includes(c.ruta)),
    { ruta: 'fechaFavorable', etiqueta: 'Fecha favorable', tipo: 'date' },
    { ruta: 'fechaAlta', etiqueta: 'Fecha de alta (cobro retenido)', tipo: 'date' },
    { ruta: 'banco', etiqueta: 'Banco' },
    { ruta: 'claveSs', etiqueta: 'Clave ANSES', indiceCrudo: 9 },
];

const COLUMNAS_FAVORABLES = ['Nombre', 'Trámite', 'Fecha', 'Banco', 'CUIL', 'Teléfono', 'G', 'Arreglo el día del cobro',
    'Referencia', 'CSS', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'Bloque'];

// NUEVO DEUDORES EN CUOTA: el cliente y el plan (cuántas cuotas y de cuánto).
const CAMPOS_EDITABLES_DEUDORES = [
    { ruta: 'cuil', etiqueta: 'CUIL' },
    { ruta: 'apellido', etiqueta: 'Apellido' },
    { ruta: 'nombre', etiqueta: 'Nombre' },
    { ruta: 'telefonoPropio', etiqueta: 'Teléfono' },
    { ruta: 'cantidadCuotas', etiqueta: 'Cantidad de cuotas' },
    { ruta: 'montoCuota', etiqueta: 'Monto de cada cuota' },
    { ruta: 'observacion', etiqueta: 'Observación del cliente' },
    { ruta: 'referencia.cuilClienteReferente', etiqueta: 'Cliente que lo refirió', buscaCliente: true },
];

const COLUMNAS_DEUDORES = ['Cliente', 'Trámite', 'CUIL', 'Teléfono', 'Referencia', 'Adeuda', 'Cuotas',
    'Cuota 1', 'Cuota 2', 'Cuota 3', 'Cuota 4', 'Cuota 5', 'Cuota 6', 'Cuota 7', 'Cuota 8', 'Cuota 9', 'Cuota 10',
    'Cuota 11', 'Cuota 12', 'Cuota 13', 'Cuota 14', 'Cuota 15', 'Bloque'];

// PARA CARGAR JAUREGUI: el cliente y la carpeta del listado (bloque, fechas y resultado).
const CAMPOS_EDITABLES_JAUREGUI = [
    { ruta: 'cuil', etiqueta: 'CUIL' },
    { ruta: 'apellido', etiqueta: 'Apellido' },
    { ruta: 'nombre', etiqueta: 'Nombre' },
    { ruta: 'telefonoPropio', etiqueta: 'Teléfono' },
    { ruta: 'fechaPase', etiqueta: 'Fecha de pase', tipo: 'date' },
    { ruta: 'fechaLlamado', etiqueta: 'Fecha del llamado', tipo: 'date' },
    {
        ruta: 'accionTramite', etiqueta: 'Qué hacer con el reajuste',
        opciones: [
            { valor: 'CREAR', etiqueta: 'Abrir un reajuste nuevo' },
            { valor: 'EXISTENTE', etiqueta: 'Usar el reajuste que ya existe' },
        ],
    },
    { ruta: 'idTramiteExistente', etiqueta: 'Reajuste existente' },
    { ruta: 'observacionCarpeta', etiqueta: 'Observación de la carpeta' },
];

const COLUMNAS_JAUREGUI = ['Casilla', 'Nombre', 'Trámite', 'CUIL', 'Teléfono', 'Referencia', 'Día de pase / vista',
    '¿Quién carga?', 'Plazo', 'Clave CSS', 'Resultado', 'Clave AFIP', 'CIDI', 'Mi Argentina', 'Sección'];

const ETIQUETA_BLOQUE_JAUREGUI = {
    PARA_CARGAR: 'Para cargar', ESPERA_TURNO: 'En espera de turno ANSES', CARGADO_SI_CONVIENE: 'Cargado · sí conviene',
    CARGADO_TRABADO: 'Cargado · no conviene o esperando documental', RETIRAR_DOCUMENTAL: 'No conviene · retirar documental',
    NO_CONVIENE_LLAMAR: 'No conviene · para llamar',
};

const COLUMNAS_PARA_APROBAR = ['Nombre', 'Trámite', 'Estado', 'CUIL', 'Referencia', 'Teléfono', 'N° expediente',
    'Fecha de ingreso', 'Cuándo generar amparo', 'Amparo por mora', 'Pronto despacho', 'Estudios médicos',
    'Junta y pericia', 'Clave SS', 'Clave fiscal', 'CiDi', 'Mi Argentina', 'Bloque'];
const COLUMNAS_AMPAROS = ['Nombre', 'Trámite', 'Estado', 'CUIL', 'Referencia', 'Teléfono', 'Pronto despacho enviado',
    'Amparo enviado', 'Columna I', 'Clave ANSES', 'CiDi', 'Estado del trámite administrativo', 'M', 'N', 'O', 'P', 'Q', 'Bloque'];

// Problemas que no son un campo editable: se nombran igual en los chips.
const ETIQUETAS_EXTRA = { ignorada: 'No se migra (decisión del estudio)', contactos: 'Contactos', referencia: 'Referencia', fechaLiquidar: 'Fecha de liquidar', motivoCancelacion: 'Se crea cancelado' };

const camposDe = (tipoLote) => {
    if (tipoLote === 'TURNERO') return CAMPOS_EDITABLES_TURNERO;
    if (tipoLote === 'LIQUIDAR') return CAMPOS_EDITABLES_LIQUIDAR;
    if (tipoLote === 'PARA_APROBAR') return CAMPOS_EDITABLES_PARA_APROBAR;
    if (tipoLote === 'FAVORABLES' || tipoLote === 'ALTAS') return CAMPOS_EDITABLES_FAVORABLES;
    if (tipoLote === 'DEUDORES') return CAMPOS_EDITABLES_DEUDORES;
    if (tipoLote === 'JAUREGUI') return CAMPOS_EDITABLES_JAUREGUI;
    if (tipoLote === 'CARPETAS_ELIMINADAS' || tipoLote === 'ARCHIVO') return CAMPOS_EDITABLES_HISTORICA;
    return CAMPOS_EDITABLES;
};

const etiquetaCampo = (ruta, tipoLote) =>
    camposDe(tipoLote).find((c) => c.ruta === ruta)?.etiqueta || ETIQUETAS_EXTRA[ruta] || ruta;

/** Las dos planillas que se suben enteras (.xlsx) y se aplican en segundo plano. */
const ES_HISTORICA = (tipo) => tipo === 'CARPETAS_ELIMINADAS' || tipo === 'ARCHIVO';
const ES_EXCEL = (tipo) => tipo === 'LIQUIDAR' || tipo === 'PARA_APROBAR' || tipo === 'FAVORABLES' || tipo === 'ALTAS' || tipo === 'DEUDORES' || tipo === 'JAUREGUI' || ES_HISTORICA(tipo);

// CARPETAS ELIMINADAS y ARCHIVO van a la planilla histórica tal cual: lo único que se corrige es el
// CUIL (para vincularla a un cliente) y el nombre.
const CAMPOS_EDITABLES_HISTORICA = [
    { ruta: 'cuil', etiqueta: 'CUIL' },
    { ruta: 'nombre', etiqueta: 'Nombre' },
];

/**
 * Los valores entre los que se puede elegir al corregir un campo, o null si es texto libre.
 *
 * La carpeta es el caso especial: sus opciones no son fijas sino los trámites que ese cliente ya
 * tiene, que la fila trae justamente para esto. Sin eso había que escribir el id a mano.
 */
function opcionesDeEdicion(edicion, tipoLote) {
    if (!edicion) return null;
    if (edicion.ruta === 'turno.subtipo') {
        // Depende del tipo de trámite de esta fila: los valores de Jubilación no sirven para
        // Reajuste ni al revés.
        return SUBTIPOS_POR_TRAMITE[edicion.fila?.datos?.turno?.tipoTramite] || null;
    }
    if (edicion.ruta === 'subtipo') {
        return SUBTIPOS_POR_TRAMITE[edicion.fila?.datos?.tipoTramite] || null;
    }
    if (edicion.ruta === 'turno.idTramiteExistente' || edicion.ruta === 'idTramiteExistente') {
        const datos = edicion.fila?.datos || {};
        const tramites = (edicion.ruta === 'idTramiteExistente' ? datos : datos.turno || {}).tramitesDelCliente || [];
        if (tramites.length === 0) return null;
        return tramites.map((t) => ({
            valor: String(t.idTramite),
            etiqueta: `#${t.idTramite} · ${t.tipoTramite || 'sin tipo'}`
                + (t.estado ? ` · ${t.estado}` : '')
                + (t.coincideConLoDeducido ? ' · coincide con lo deducido' : ''),
        }));
    }
    return camposDe(tipoLote).find((c) => c.ruta === edicion.ruta)?.opciones || null;
}

/**
 * Qué ruta editable arregla el problema de un campo dado.
 *
 * No siempre coinciden: el aviso de la referencia viene con campo "referencia" y lo que hay que
 * completar es "referencia.cuilClienteReferente". Sin esto el aviso que pide confirmar contra qué
 * cliente se vincula no ofrecía ningún link para hacerlo.
 */
function rutaEditablePara(campo, tipoLote) {
    const campos = camposDe(tipoLote);
    if (campos.some((c) => c.ruta === campo)) return campo;
    return campos.find((c) => c.ruta.startsWith(`${campo}.`))?.ruta || null;
}

/**
 * Con qué valor abre el modal.
 *
 * Normalmente el que ya tiene la fila. Pero las claves que el parser descartó quedan en null, y
 * pedirle a alguien que las reescriba de memoria no tiene sentido: se precarga lo que decía la
 * planilla para que solo tenga que recortarlo.
 */
function valorInicial(fila, ruta, tipoLote) {
    const actual = leerValor(fila.datos, ruta);
    if (actual !== null && actual !== undefined && actual !== '') return actual;

    const campo = camposDe(tipoLote).find((c) => c.ruta === ruta);
    if (campo?.indiceCrudo != null) {
        return (fila.datosCrudos || [])[campo.indiceCrudo] || '';
    }
    return '';
}

/** Arma el objeto anidado que espera el backend: "turno.fecha" -> { turno: { fecha: v } }. */
function armarEdicion(ruta, valor) {
    const partes = ruta.split('.');
    if (partes.length === 1) return { [ruta]: valor };
    return { [partes[0]]: { [partes[1]]: valor } };
}

function leerValor(datos, ruta) {
    return ruta.split('.').reduce((obj, parte) => (obj == null ? undefined : obj[parte]), datos);
}

/**
 * Importación por lote: subir la planilla, revisarla y corregirla acá, y recién después aplicarla.
 *
 * Subir NO crea nada: deja el lote en borrador. Lo que se corrige queda guardado, así que se puede
 * cerrar la pantalla y seguir después — que es lo que antes obligaba a rehacer el Excel entero.
 */
function ImportacionLotePage() {
    const [lotes, setLotes] = useState([]);
    const [lote, setLote] = useState(null);
    const [filas, setFilas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [subiendo, setSubiendo] = useState(false);
    const [archivo, setArchivo] = useState(null);
    const [tipoArchivo, setTipoArchivo] = useState('TURNOS_ANSES');
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroCampo, setFiltroCampo] = useState('');
    const [busqueda, setBusqueda] = useState('');

    const [edicion, setEdicion] = useState(null);   // { fila, ruta, valor }
    const [masiva, setMasiva] = useState(null);     // { campo, valor }
    const [confirmando, setConfirmando] = useState(false);
    const [resultado, setResultado] = useState(null);
    // Cuántas tarjetas se muestran: la solapa LIQUIDAR tiene más de mil filas.
    const [limite, setLimite] = useState(150);

    const cargarLotes = useCallback(async () => {
        try {
            setLotes(await listarLotes());
        } catch (e) {
            toast.error('No se pudieron cargar las importaciones.');
        }
    }, []);

    useEffect(() => { cargarLotes(); }, [cargarLotes]);

    const abrirLote = useCallback(async (idLote) => {
        setCargando(true);
        try {
            const [cabecera, listaFilas] = await Promise.all([
                obtenerLote(idLote),
                listarFilas(idLote),
            ]);
            setLote(cabecera);
            setFilas(listaFilas);
            setResultado(null);
            setLimite(150);
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo abrir el lote.');
        } finally {
            setCargando(false);
        }
    }, []);

    // Borrar un borrador: el caso real es haberse equivocado de archivo o de tipo. No creó nada,
    // así que no hay nada que revertir; lo único que se pierde son las correcciones de ese lote.
    const handleEliminar = async (l) => {
        const confirmado = window.confirm(
            `¿Borrar la importación #${l.idLote} (${l.totalFilas} filas) de "${l.nombreArchivo}"?

`
            + 'No se creó nada todavía, así que no hay nada que revertir. Se pierden las correcciones de este lote.');
        if (!confirmado) return;
        try {
            await eliminarLote(l.idLote);
            toast.success(`Importación #${l.idLote} borrada.`);
            if (lote?.idLote === l.idLote) {
                setLote(null);
                setFilas([]);
                setResultado(null);
            }
            await cargarLotes();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo borrar la importación.');
        }
    };

    const handleSubir = async () => {
        if (!archivo) {
            toast.error('Elegí primero el archivo.');
            return;
        }
        setSubiendo(true);
        try {
            const creado = await crearLote(archivo, tipoArchivo);
            toast.success(`Archivo leído: ${creado.totalFilas} filas. Todavía no se creó nada.`);
            setArchivo(null);
            await cargarLotes();
            await abrirLote(creado.idLote);
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo leer el archivo.');
        } finally {
            setSubiendo(false);
        }
    };

    const refrescar = useCallback(async () => {
        if (!lote) return;
        const [cabecera, listaFilas] = await Promise.all([
            obtenerLote(lote.idLote),
            listarFilas(lote.idLote),
        ]);
        setLote(cabecera);
        setFilas(listaFilas);
    }, [lote]);

    const handleGuardarEdicion = async () => {
        if (!edicion) return;
        try {
            // Vacío se manda como null y no como "": hay campos numéricos (el id del trámite) que
            // no aceptan cadena vacía, y "sin elegir" es justamente null.
            const valor = typeof edicion.valor === 'string' && edicion.valor.trim() === ''
                ? null : edicion.valor;
            const actualizada = await editarFila(
                lote.idLote, edicion.fila.idFila, armarEdicion(edicion.ruta, valor));
            setFilas((previas) =>
                previas.map((f) => (f.idFila === actualizada.idFila ? actualizada : f)));
            setEdicion(null);
            const cabecera = await obtenerLote(lote.idLote);
            setLote(cabecera);
            toast.success('Fila corregida.');
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo guardar la corrección.');
        }
    };

    /**
     * El texto que estaba en una columna de clave no era una clave: se lo manda a observaciones.
     *
     * No alcanza con descartarlo —eso ya lo hace el parser y por eso avisa—: lo que decía la
     * planilla se pierde. Acá se conserva en la observación del cliente y, de paso, editar el
     * campo de la clave apaga el aviso, porque el backend deja de mostrar los avisos de un campo
     * que alguien ya resolvió a mano.
     */
    const handleAObservacion = async (fila, campo) => {
        const indice = camposDe(lote.tipo).find((c) => c.ruta === campo)?.indiceCrudo;
        const crudo = indice != null ? (fila.datosCrudos || [])[indice] : null;
        if (!crudo) {
            toast.error('La planilla no tiene texto en esa columna.');
            return;
        }
        const previa = fila.datos?.observacion;
        try {
            const actualizada = await editarFila(lote.idLote, fila.idFila, {
                observacion: previa ? `${previa} · ${crudo}` : crudo,
                [campo]: null,
            });
            setFilas((previas) =>
                previas.map((f) => (f.idFila === actualizada.idFila ? actualizada : f)));
            setLote(await obtenerLote(lote.idLote));
            toast.success('Se pasó a la observación del cliente.');
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo mover el texto.');
        }
    };

    const handleIgnorar = async (fila, ignorar) => {
        try {
            const actualizada = await ignorarFila(lote.idLote, fila.idFila, ignorar);
            setFilas((previas) =>
                previas.map((f) => (f.idFila === actualizada.idFila ? actualizada : f)));
            setLote(await obtenerLote(lote.idLote));
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo cambiar la fila.');
        }
    };

    // Lote ya confirmado: la fila que quedó con error se corrigió (mirando la carpeta física) y se
    // migra sola, sin volver a subir el archivo.
    const [migrandoFila, setMigrandoFila] = useState(null);
    const handleMigrarFila = async (fila) => {
        setMigrandoFila(fila.idFila);
        try {
            await aplicarFila(lote.idLote, fila.idFila);
            toast.success(`Fila ${fila.nroFila} migrada.`);
            await refrescar();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo migrar la fila.');
            await refrescar();
        } finally {
            setMigrandoFila(null);
        }
    };

    const handleMasiva = async () => {
        if (!masiva?.campo) return;
        try {
            const r = await corregirEnMasa(lote.idLote, masiva.campo, armarEdicion(masiva.campo, masiva.valor));
            toast.success(`${r.filasCorregidas} filas corregidas.`);
            setMasiva(null);
            await refrescar();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo aplicar la corrección masiva.');
        }
    };

    const handleConfirmar = async () => {
        setConfirmando(true);
        try {
            const r = await confirmarLote(lote.idLote);
            if (r?.enSegundoPlano) {
                // LIQUIDAR: el servidor aplica en segundo plano (tarda más de dos minutos). El
                // efecto de abajo sigue el avance hasta que el lote queda confirmado.
                toast.info('Aplicando el lote. Podés seguir el avance acá; no cierres el sistema.');
                setLote(await obtenerLote(lote.idLote));
                return;
            }
            setResultado(r);
            toast.success(`${r.aplicadas} filas aplicadas.`);
            await refrescar();
            await cargarLotes();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo confirmar el lote.');
        } finally {
            setConfirmando(false);
        }
    };

    // Mientras el servidor aplica en segundo plano se consulta el lote cada 3 s: los chips muestran
    // cuántas filas ya se aplicaron. Depende solo del id y de la bandera, no del objeto lote, para
    // no reiniciar el intervalo en cada consulta.
    const idLoteAbierto = lote?.idLote;
    const aplicandoEnFondo = !!lote?.confirmando;
    useEffect(() => {
        if (!idLoteAbierto || !aplicandoEnFondo) return undefined;
        const id = setInterval(async () => {
            try {
                const cabecera = await obtenerLote(idLoteAbierto);
                setLote(cabecera);
                if (!cabecera.confirmando) {
                    setFilas(await listarFilas(idLoteAbierto));
                    await cargarLotes();
                    const c = cabecera.conteoPorEstado || {};
                    toast.success(`Lote aplicado: ${c.APLICADA || 0} filas.`
                        + (c.ERROR ? ` ${c.ERROR} quedaron con error.` : ''));
                }
            } catch {
                // Un corte momentáneo no frena el seguimiento: se reintenta en la próxima vuelta.
            }
        }, 3000);
        return () => clearInterval(id);
    }, [idLoteAbierto, aplicandoEnFondo, cargarLotes]);

    // Los campos con problemas y cuántas filas tiene cada uno: son los chips que permiten atacar
    // un defecto repetido de una vez en lugar de fila por fila.
    const problemasPorCampo = useMemo(() => {
        const conteo = new Map();
        filas.forEach((f) => (f.problemas || []).forEach((p) => {
            conteo.set(p.campo, (conteo.get(p.campo) || 0) + 1);
        }));
        return Array.from(conteo.entries()).sort((a, b) => b[1] - a[1]);
    }, [filas]);

    const filasVisibles = useMemo(() => {
        return filas.filter((f) => {
            if (filtroEstado && f.estado !== filtroEstado) return false;
            if (filtroCampo && !(f.problemas || []).some((p) => p.campo === filtroCampo)) return false;
            const d = f.datos || {};
            return coincideBusqueda([d.apellido, d.nombre, d.cuil, d.textoEstado], busqueda);
        });
    }, [filas, filtroEstado, filtroCampo, busqueda]);

    const conteo = lote?.conteoPorEstado || {};
    const esBorrador = lote?.estado === 'BORRADOR';
    const esConfirmado = lote?.estado === 'CONFIRMADO' && !lote?.confirmando;
    const aplicables = (conteo.OK || 0) + (conteo.ADVERTENCIA || 0);
    const pendientes = (conteo.ERROR || 0) + (conteo.OK || 0) + (conteo.ADVERTENCIA || 0);

    return (
        <div className="homepage-container">
            <ToastContainer position="top-right" autoClose={4000} />

            <div className="homepage-header">
                <div>
                    <h1 className="homepage-title">
                        <i className="bi bi-file-earmark-arrow-up me-3"></i>
                        Importación por lote
                    </h1>
                    <p className="homepage-subtitle">
                        Subí la planilla, revisá y corregí lo que haga falta acá mismo, y recién
                        después aplicá. <strong>Subir no crea nada</strong>: hasta que no confirmes,
                        el lote es un borrador y lo que corregís queda guardado.
                    </p>
                </div>
            </div>

            {/* Alta */}
            <div className="lote-panel">
                <div className="lote-subir">
                    <Form.Select
                        style={{ maxWidth: 260 }}
                        value={tipoArchivo}
                        onChange={(e) => setTipoArchivo(e.target.value)}
                        disabled={subiendo}
                    >
                        <option value="TURNOS_ANSES">Planilla TURNOS ANSES</option>
                        <option value="TURNERO">Planilla del turnero</option>
                        <option value="LIQUIDAR">LIQUIDAR (Excel completo)</option>
                        <option value="PARA_APROBAR">PARA APROBAR (Excel completo)</option>
                        <option value="FAVORABLES">FAVORABLES (Excel completo)</option>
                        <option value="ALTAS">ALTAS del mes (Excel completo)</option>
                        <option value="DEUDORES">NUEVO DEUDORES EN CUOTA (Excel completo)</option>
                        <option value="JAUREGUI">PARA CARGAR JAUREGUI (Excel completo)</option>
                        <option value="CARPETAS_ELIMINADAS">CARPETAS ELIMINADAS (Excel completo)</option>
                        <option value="ARCHIVO">ARCHIVO (Excel completo)</option>
                    </Form.Select>
                    <Form.Control
                        type="file"
                        key={tipoArchivo}
                        accept={ES_EXCEL(tipoArchivo) ? '.xlsx' : '.csv,text/csv'}
                        onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                        disabled={subiendo}
                    />
                    <Button className="rounded-pill px-4" onClick={handleSubir} disabled={subiendo || !archivo}>
                        {subiendo
                            ? <><Spinner animation="border" size="sm" className="me-2" />Leyendo…</>
                            : <><i className="bi bi-upload me-2" />Subir y revisar</>}
                    </Button>
                </div>

                {ES_EXCEL(tipoArchivo) && (
                    <div className="lote-aviso-orden">
                        <i className="bi bi-info-circle me-2"></i>
                        Subí el <strong>Excel entero</strong> (.xlsx), no un CSV: se lee
                        {tipoArchivo === 'LIQUIDAR' && ' LIQUIDAR con sus bloques por mes'}
                        {tipoArchivo === 'PARA_APROBAR' && ' PARA APROBAR con sus secciones'}
                        {tipoArchivo === 'FAVORABLES' && ' FAVORABLES con sus bloques (cada fila pasa a Cerrado favorable y deja su cobro en Favorables)'}
                        {tipoArchivo === 'ALTAS' && ' la solapa ALTAS del mes (cada cobro queda en Altas con su día de cobro; conviene importar antes FAVORABLES)'}
                        {tipoArchivo === 'DEUDORES' && ' NUEVO DEUDORES EN CUOTA con sus bloques por mes (cobros en cuotas con sus pagos, recordatorio apagado; conviene importar antes ALTAS)'}
                        {tipoArchivo === 'JAUREGUI' && ' PARA CARGAR JAUREGUI con sus secciones (cada reajuste queda marcado en el listado, en su bloque)'}
                        {ES_HISTORICA(tipoArchivo) && ` ${tipoArchivo === 'ARCHIVO' ? 'ARCHIVO' : 'CARPETAS ELIMINADAS'} tal cual (no crea clientes: va a la planilla histórica)`},
                        las fechas y los acentos. Leerlo tarda unos 30 segundos.
                        {tipoArchivo === 'PARA_APROBAR' && (
                            <> Conviene importar <strong>primero LIQUIDAR</strong>: si un cliente está en las dos,
                                PARA APROBAR completa su trámite en vez de duplicarlo.</>
                        )}
                    </div>
                )}

                {tipoArchivo === 'TURNERO' && (
                    <div className="lote-aviso-orden">
                        <i className="bi bi-info-circle me-2"></i>
                        Conviene importar primero la planilla <strong>TURNOS ANSES</strong>: las filas
                        de ANSES de este archivo corrigen la citación y la escribanía de turnos que
                        el otro lote crea, y si todavía no existen no se van a poder cruzar.
                    </div>
                )}

                {lotes.length > 0 && (
                    <div className="lote-recientes">
                        <span className="text-muted small me-2">Importaciones:</span>
                        {lotes.slice(0, 6).map((l) => (
                            <span key={l.idLote} className="lote-pill-grupo">
                                <button
                                    className={`lote-pill ${lote?.idLote === l.idLote ? 'lote-pill-activa' : ''}`}
                                    onClick={() => abrirLote(l.idLote)}
                                    title={l.nombreArchivo}
                                >
                                    #{l.idLote} · {l.totalFilas} filas
                                    <span className={`lote-pill-estado lote-pill-${l.estado.toLowerCase()}`}>
                                        {l.estado === 'BORRADOR' ? 'borrador' : l.estado.toLowerCase()}
                                    </span>
                                </button>
                                {/* Una importación confirmada no se borra: es el registro de lo aplicado. */}
                                {l.estado !== 'CONFIRMADO' && (
                                    <button
                                        className="lote-pill-borrar"
                                        onClick={() => handleEliminar(l)}
                                        title={`Borrar la importación #${l.idLote}`}
                                        aria-label={`Borrar la importación #${l.idLote}`}
                                    >
                                        ×
                                    </button>
                                )}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {cargando && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            )}

            {lote && !cargando && (
                <>
                    {/* Resumen y filtros */}
                    <div className="lote-panel">
                        <div className="lote-cabecera">
                            <div>
                                <div className="lote-archivo">
                                    <i className={`bi ${ES_EXCEL(lote.tipo) ? 'bi-file-earmark-excel' : 'bi-filetype-csv'} me-2`}></i>
                                    {lote.nombreArchivo}
                                </div>
                                <div className="text-muted small">
                                    Lote #{lote.idLote} · {lote.totalFilas} filas ·{' '}
                                    {lote.confirmando
                                        ? `aplicando… ${conteo.APLICADA || 0} de ${aplicables + (conteo.APLICADA || 0)} filas`
                                        : lote.estado === 'BORRADOR'
                                            ? 'borrador (todavía no se creó nada)'
                                            : `confirmado${lote.usuario ? ` por ${lote.usuario}` : ''}`}
                                </div>
                            </div>
                            {esBorrador && (
                                <Button
                                    variant="success"
                                    className="rounded-pill px-4"
                                    onClick={handleConfirmar}
                                    disabled={confirmando || aplicandoEnFondo || aplicables === 0}
                                    title={aplicables === 0 ? 'No hay filas aplicables' : ''}
                                >
                                    {confirmando || aplicandoEnFondo
                                        ? <><Spinner animation="border" size="sm" className="me-2" />Aplicando…</>
                                        : <><i className="bi bi-check-lg me-2" />Confirmar {aplicables} filas</>}
                                </Button>
                            )}
                        </div>

                        <div className="lote-chips">
                            <button
                                className={`lote-chip ${!filtroEstado ? 'lote-chip-activo' : ''}`}
                                onClick={() => setFiltroEstado('')}
                            >
                                Todas <span className="lote-chip-n">{lote.totalFilas}</span>
                            </button>
                            {Object.entries(META_ESTADO).map(([clave, meta]) => (
                                conteo[clave] ? (
                                    <button
                                        key={clave}
                                        className={`lote-chip ${meta.clase} ${filtroEstado === clave ? 'lote-chip-activo' : ''}`}
                                        onClick={() => setFiltroEstado(filtroEstado === clave ? '' : clave)}
                                    >
                                        <i className={`bi ${meta.icono} me-1`}></i>
                                        {meta.etiqueta} <span className="lote-chip-n">{conteo[clave]}</span>
                                    </button>
                                ) : null
                            ))}
                        </div>

                        {problemasPorCampo.length > 0 && (
                            <div className="lote-chips lote-chips-campos">
                                <span className="text-muted small me-1 align-self-center">Por problema:</span>
                                {problemasPorCampo.map(([campo, n]) => (
                                    <button
                                        key={campo}
                                        className={`lote-chip lote-chip-campo ${filtroCampo === campo ? 'lote-chip-activo' : ''}`}
                                        onClick={() => setFiltroCampo(filtroCampo === campo ? '' : campo)}
                                    >
                                        {etiquetaCampo(campo, lote.tipo)} <span className="lote-chip-n">{n}</span>
                                    </button>
                                ))}
                                {filtroCampo && esBorrador && camposDe(lote.tipo).some((c) => c.ruta === filtroCampo) && (
                                    <button
                                        className="lote-chip lote-chip-masiva"
                                        onClick={() => setMasiva({ campo: filtroCampo, valor: '' })}
                                    >
                                        <i className="bi bi-lightning-charge me-1"></i>
                                        Corregir las {problemasPorCampo.find(([c]) => c === filtroCampo)?.[1]} de una vez
                                    </button>
                                )}
                            </div>
                        )}

                        <Form.Control
                            className="mt-3"
                            placeholder="Buscar por nombre, apellido o CUIL…"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                        />
                    </div>

                    {esConfirmado && pendientes > 0 && (
                        <div className="alert alert-warning border-0 rounded-3 d-flex flex-wrap align-items-center gap-2">
                            <i className="bi bi-tools" />
                            <span className="flex-grow-1">
                                Quedaron <strong>{pendientes}</strong> filas sin migrar. Corregí cada una (mirando la carpeta
                                física) y migrala con <strong>Migrar esta fila</strong>, sin volver a subir el archivo.
                            </span>
                            {conteo.ERROR > 0 && (
                                <Button size="sm" variant="outline-dark" className="rounded-pill"
                                        onClick={() => setFiltroEstado(filtroEstado === 'ERROR' ? '' : 'ERROR')}>
                                    {filtroEstado === 'ERROR' ? 'Ver todas' : `Ver las ${conteo.ERROR} con error`}
                                </Button>
                            )}
                        </div>
                    )}

                    {resultado && (
                        <div className="lote-resultado">
                            <div className="lote-resultado-titulo">
                                <i className="bi bi-check-circle-fill me-2"></i>
                                {resultado.aplicadas} filas aplicadas
                                {resultado.salteadas > 0 && ` · ${resultado.salteadas} salteadas`}
                                {resultado.fallidas > 0 && ` · ${resultado.fallidas} fallidas`}
                            </div>
                            {(resultado.errores || []).map((e, i) => (
                                <div key={i} className="lote-resultado-error">{e}</div>
                            ))}
                        </div>
                    )}

                    {/* Filas */}
                    <div className="lote-filas">
                        {filasVisibles.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3">
                                No hay filas que coincidan con el filtro.
                            </div>
                        ) : filasVisibles.slice(0, limite).map((fila) => {
                            // En un lote confirmado, lo que no se migró sigue siendo editable.
                            const editable = esBorrador || (esConfirmado && fila.estado !== 'APLICADA');
                            const props = {
                                fila,
                                esBorrador: editable,
                                onEditar: (ruta) => setEdicion({
                                    fila,
                                    ruta,
                                    valor: valorInicial(fila, ruta, lote.tipo),
                                }),
                                onIgnorar: handleIgnorar,
                                onAObservacion: handleAObservacion,
                            };
                            let tarjeta;
                            if (lote.tipo === 'TURNERO') tarjeta = <FilaLoteTurnero {...props} />;
                            else if (lote.tipo === 'LIQUIDAR') tarjeta = <FilaLoteLiquidar {...props} />;
                            else if (lote.tipo === 'PARA_APROBAR') tarjeta = <FilaLoteParaAprobar {...props} />;
                            else if (lote.tipo === 'FAVORABLES' || lote.tipo === 'ALTAS') tarjeta = <FilaLoteParaAprobar {...props} tipoLote={lote.tipo} />;
                            else if (lote.tipo === 'DEUDORES') tarjeta = <FilaLoteDeudor {...props} />;
                            else if (lote.tipo === 'JAUREGUI') tarjeta = <FilaLoteJauregui {...props} />;
                            else if (ES_HISTORICA(lote.tipo)) tarjeta = <FilaLoteHistorica {...props} />;
                            else tarjeta = <FilaLote {...props} />;
                            const migrable = esConfirmado && (fila.estado === 'OK' || fila.estado === 'ADVERTENCIA');
                            return (
                                <div key={fila.idFila}>
                                    {tarjeta}
                                    {migrable && (
                                        <div className="d-flex justify-content-end mt-n2 mb-3">
                                            <Button size="sm" variant="success" className="rounded-pill px-3"
                                                    disabled={migrandoFila === fila.idFila}
                                                    onClick={() => handleMigrarFila(fila)}>
                                                {migrandoFila === fila.idFila
                                                    ? <><Spinner animation="border" size="sm" className="me-2" />Migrando…</>
                                                    : <><i className="bi bi-box-arrow-in-right me-1" />Migrar esta fila</>}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {filasVisibles.length > limite && (
                            <div className="text-center my-3">
                                <Button variant="outline-primary" className="rounded-pill px-4"
                                        onClick={() => setLimite(limite + 150)}>
                                    Mostrar más ({filasVisibles.length - limite} restantes)
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Edición de un campo */}
            <Modal show={!!edicion} onHide={() => setEdicion(null)} centered className="lote-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Corregir {edicion && etiquetaCampo(edicion.ruta, lote?.tipo)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {edicion && (
                        <>
                            <div className="text-muted small mb-2">
                                Fila {edicion.fila.nroFila} · {apellidoNombre(edicion.fila.datos?.apellido, edicion.fila.datos?.nombre)}
                            </div>
                            {camposDe(lote?.tipo).find((c) => c.ruta === edicion.ruta)?.buscaCliente ? (
                                <BuscadorCliente
                                    valor={edicion.valor}
                                    onElegir={(cuil) => setEdicion({ ...edicion, valor: cuil })}
                                />
                            ) : opcionesDeEdicion(edicion, lote?.tipo) ? (
                                <Form.Select
                                    autoFocus
                                    value={edicion.valor}
                                    onChange={(e) => setEdicion({ ...edicion, valor: e.target.value })}
                                >
                                    <option value="">— sin elegir —</option>
                                    {opcionesDeEdicion(edicion, lote?.tipo).map((o) => (
                                        <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
                                    ))}
                                </Form.Select>
                            ) : (
                                <Form.Control
                                    autoFocus
                                    type={camposDe(lote?.tipo).find((c) => c.ruta === edicion.ruta)?.tipo || 'text'}
                                    value={edicion.valor}
                                    onChange={(e) => setEdicion({ ...edicion, valor: e.target.value })}
                                    onKeyDown={(e) => e.key === 'Enter' && handleGuardarEdicion()}
                                />
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" className="rounded-pill" onClick={() => setEdicion(null)}>
                        Cancelar
                    </Button>
                    <Button className="rounded-pill" onClick={handleGuardarEdicion}>Guardar</Button>
                </Modal.Footer>
            </Modal>

            {/* Corrección masiva */}
            <Modal show={!!masiva} onHide={() => setMasiva(null)} centered className="lote-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Corregir en masa</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {masiva && (
                        <>
                            <p className="mb-2">
                                Se va a poner el mismo valor en <strong>{etiquetaCampo(masiva.campo, lote?.tipo)}</strong> en
                                todas las filas que tengan un problema en ese campo.
                            </p>
                            <Form.Control
                                autoFocus
                                placeholder="Valor para todas"
                                value={masiva.valor}
                                onChange={(e) => setMasiva({ ...masiva, valor: e.target.value })}
                            />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" className="rounded-pill" onClick={() => setMasiva(null)}>
                        Cancelar
                    </Button>
                    <Button className="rounded-pill" onClick={handleMasiva}>Aplicar a todas</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

/** Una fila del lote: lo que se va a crear, lo que hay que mirarle y lo que decía la planilla. */
function FilaLote({ fila, esBorrador, onEditar, onIgnorar, onAObservacion }) {
    const [verCrudos, setVerCrudos] = useState(false);
    const d = fila.datos || {};
    const t = d.turno || {};
    const meta = META_ESTADO[fila.estado] || META_ESTADO.OK;

    return (
        <div className={`lote-fila lote-fila-${fila.estado.toLowerCase()}`}>
            <div className="lote-fila-cabecera">
                <div className="lote-fila-titulo">
                    <span className="lote-fila-nro">#{fila.nroFila}</span>
                    <strong>{d.apellido || '—'}, {d.nombre || '—'}</strong>
                    <LapizCabecera onEditar={esBorrador ? () => onEditar('apellido') : null}
                                   titulo="Corregir el apellido" />
                    <LapizCabecera onEditar={esBorrador ? () => onEditar('nombre') : null}
                                   titulo="Corregir el nombre" />
                    <span className="text-muted small ms-2">{d.cuil || 'sin CUIL'}</span>
                    <LapizCabecera onEditar={esBorrador ? () => onEditar('cuil') : null}
                                   titulo="Corregir el CUIL" />
                    <span className={`lote-badge ${meta.clase}`}>
                        <i className={`bi ${meta.icono} me-1`}></i>{meta.etiqueta}
                    </span>
                </div>
                <div className="lote-fila-acciones">
                    <button className="lote-link" onClick={() => setVerCrudos(!verCrudos)}>
                        <i className="bi bi-table me-1"></i>{verCrudos ? 'Ocultar' : 'Ver'} planilla
                    </button>
                    {esBorrador && fila.estado !== 'APLICADA' && (
                        <button
                            className="lote-link"
                            onClick={() => onIgnorar(fila, fila.estado !== 'IGNORADA')}
                        >
                            <i className="bi bi-slash-circle me-1"></i>
                            {fila.estado === 'IGNORADA' ? 'Volver a incluir' : 'Saltear'}
                        </button>
                    )}
                </div>
            </div>

            <div className="lote-fila-datos">
                <Dato etiqueta="Turno" valor={`${t.fecha || '—'} ${t.horaAnses || ''}`}
                      onEditar={esBorrador ? () => onEditar('turno.fecha') : null} />
                <Dato etiqueta="UDAI" valor={t.udai}
                      onEditar={esBorrador ? () => onEditar('turno.udai') : null} />
                <Dato etiqueta="Trámite" valor={t.tipoTramite}
                      onEditar={esBorrador ? () => onEditar('turno.tipoTramite') : null} />
                {t.subtipo && (
                    <Dato etiqueta="Subtipo" valor={t.subtipo}
                          onEditar={esBorrador ? () => onEditar('turno.subtipo') : null} />
                )}
                <Dato etiqueta="Teléfono" valor={d.telefonoPropio}
                      onEditar={esBorrador ? () => onEditar('telefonoPropio') : null} />
                <Dato etiqueta="Carpeta" valor={
                    t.accionTramite === 'EXISTENTE'
                        ? `usa el trámite #${t.idTramiteExistente ?? '?'}`
                        : t.accionTramite === 'SOLO_TURNO' ? 'sin carpeta' : 'abre una nueva'}
                      onEditar={esBorrador ? () => onEditar('turno.accionTramite') : null} />
                {t.accionTramite === 'EXISTENTE' && (
                    <Dato etiqueta="Trámite" valor={t.idTramiteExistente ? `#${t.idTramiteExistente}` : 'sin elegir'}
                          onEditar={esBorrador ? () => onEditar('turno.idTramiteExistente') : null} />
                )}
                {t.esPasado && <Dato etiqueta="" valor="turno pasado → se carga como realizado" />}
            </div>

            {(d.contactos || []).length > 0 && (
                <div className="lote-contactos">
                    <i className="bi bi-people me-1"></i>
                    {d.contactos.map((c, i) => (
                        <span key={i} className="lote-contacto">
                            {c.telefono}{c.relacion ? ` (${c.relacion}${c.nombre ? ` ${c.nombre}` : ''})` : ''}
                        </span>
                    ))}
                </div>
            )}

            {(fila.problemas || []).length > 0 && (
                <div className="lote-problemas">
                    {fila.problemas.map((p, i) => (
                        <div key={i} className={`lote-problema lote-problema-${p.severidad.toLowerCase()}`}>
                            <i className={`bi ${p.severidad === 'ERROR' ? 'bi-x-octagon-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                            <span>{p.mensaje}</span>
                            {esBorrador && rutaEditablePara(p.campo, 'TURNOS_ANSES') && (
                                <button className="lote-link ms-2"
                                        onClick={() => onEditar(rutaEditablePara(p.campo, 'TURNOS_ANSES'))}>
                                    corregir
                                </button>
                            )}
                            {esBorrador && onAObservacion
                                && CAMPOS_EDITABLES.find((c) => c.ruta === p.campo)?.indiceCrudo != null && (
                                <button className="lote-link ms-2" onClick={() => onAObservacion(fila, p.campo)}>
                                    mandar a observaciones
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {verCrudos && (
                <div className="lote-crudos">
                    {(fila.datosCrudos || []).map((c, i) => (
                        c ? <span key={i} className="lote-crudo"><b>{i}</b> {c}</span> : null
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Una fila de la planilla del turnero.
 *
 * Se ve distinta según lo que hace: la mayoría dan de alta un turno común, pero las de ANSES no
 * crean nada — corrigen la citación y la escribanía de una ida que ya está cargada. Esa
 * diferencia es lo primero que tiene que quedar claro al revisar, así que va en un badge y no
 * escondida entre los datos.
 */
function FilaLoteTurnero({ fila, esBorrador, onEditar, onIgnorar }) {
    const [verCrudos, setVerCrudos] = useState(false);
    const d = fila.datos || {};
    const meta = META_ESTADO[fila.estado] || META_ESTADO.OK;
    const corrigeAnses = d.accion === 'CORREGIR_TURNO_ANSES';

    return (
        <div className={`lote-fila lote-fila-${fila.estado.toLowerCase()}`}>
            <div className="lote-fila-cabecera">
                <div className="lote-fila-titulo">
                    <span className="lote-fila-nro">#{fila.nroFila}</span>
                    <strong>{d.apellido || '—'}{d.nombre ? `, ${d.nombre}` : ''}</strong>
                    <LapizCabecera onEditar={esBorrador ? () => onEditar('apellido') : null}
                                   titulo="Corregir el apellido" />
                    <LapizCabecera onEditar={esBorrador ? () => onEditar('nombre') : null}
                                   titulo="Corregir el nombre" />
                    <span className={`lote-badge ${meta.clase}`}>
                        <i className={`bi ${meta.icono} me-1`}></i>{meta.etiqueta}
                    </span>
                    {corrigeAnses && (
                        <span className="lote-badge lote-badge-anses">
                            <i className="bi bi-building me-1"></i>corrige turno ANSES
                        </span>
                    )}
                    {d.vinoDelBot && (
                        <span className="lote-badge lote-badge-bot">
                            <i className="bi bi-robot me-1"></i>bot
                        </span>
                    )}
                </div>
                <div className="lote-fila-acciones">
                    <button className="lote-link" onClick={() => setVerCrudos(!verCrudos)}>
                        <i className="bi bi-table me-1"></i>{verCrudos ? 'Ocultar' : 'Ver'} planilla
                    </button>
                    {esBorrador && fila.estado !== 'APLICADA' && (
                        <button className="lote-link" onClick={() => onIgnorar(fila, fila.estado !== 'IGNORADA')}>
                            <i className="bi bi-slash-circle me-1"></i>
                            {fila.estado === 'IGNORADA' ? 'Volver a incluir' : 'Saltear'}
                        </button>
                    )}
                </div>
            </div>

            <div className="lote-fila-datos">
                <Dato etiqueta="Fecha" valor={d.fecha}
                      onEditar={esBorrador ? () => onEditar('fecha') : null} />
                <Dato etiqueta={corrigeAnses ? 'Citación real' : 'Hora'} valor={d.horario}
                      onEditar={esBorrador ? () => onEditar('horario') : null} />
                {corrigeAnses ? (
                    <>
                        <Dato etiqueta="En ANSES" valor={d.horaAnses} />
                        <Dato etiqueta="Escribanía" valor={d.horaEscribania}
                              onEditar={esBorrador ? () => onEditar('horaEscribania') : null} />
                        <Dato etiqueta="Turno" valor={d.idTurnoAnses ? `#${d.idTurnoAnses}` : 'sin cruzar'}
                              onEditar={esBorrador ? () => onEditar('idTurnoAnses') : null} />
                    </>
                ) : (
                    <>
                        <Dato etiqueta="Tipo" valor={d.tipoTurno}
                              onEditar={esBorrador ? () => onEditar('tipoTurno') : null} />
                        <Dato etiqueta="Primera vez" valor={
                            d.esPrimeraVez == null ? 'no registrado' : d.esPrimeraVez ? 'sí' : 'no'} />
                        {d.tieneCarpeta && <Dato etiqueta="" valor="ya tiene carpeta" />}
                        <Dato etiqueta="Teléfono" valor={d.telefono}
                              onEditar={esBorrador ? () => onEditar('telefono') : null} />
                    </>
                )}
            </div>

            {d.escribaniaObservacion && (
                <div className="lote-contactos">
                    <i className="bi bi-pen me-1"></i>{d.escribaniaObservacion}
                </div>
            )}
            {d.observacion && (
                <div className="lote-contactos">
                    <i className="bi bi-chat-left-text me-1"></i>{d.observacion}
                </div>
            )}

            {(fila.problemas || []).length > 0 && (
                <div className="lote-problemas">
                    {fila.problemas.map((p, i) => (
                        <div key={i} className={`lote-problema lote-problema-${p.severidad.toLowerCase()}`}>
                            <i className={`bi ${p.severidad === 'ERROR' ? 'bi-x-octagon-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                            <span>{p.mensaje}</span>
                            {esBorrador && rutaEditablePara(p.campo, 'TURNERO') && (
                                <button className="lote-link ms-2" onClick={() => onEditar(p.campo)}>
                                    corregir
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {verCrudos && (
                <div className="lote-crudos">
                    {(fila.datosCrudos || []).map((c, i) => (
                        c ? <span key={i} className="lote-crudo"><b>{i}</b> {c}</span> : null
                    ))}
                </div>
            )}
        </div>
    );
}

const ORIGEN_FECHA = {
    BLOQUE_MES: 'mes del bloque de la planilla',
    TEXTO: 'sacada del texto',
    SIN_FECHA: 'sin fecha vigente',
};

const formatoFechaCorta = (iso) => {
    if (!iso) return null;
    const [a, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}/${m}/${a}`;
};

/**
 * Una fila de la solapa LIQUIDAR.
 *
 * Lo primero que se lee es el estado del trámite tal cual lo escribió el estudio: es de donde sale
 * todo lo demás (el tipo, la fecha, las próximas fechas), así que quien revisa tiene que poder
 * compararlo con lo deducido sin abrir nada.
 */
function FilaLoteLiquidar({ fila, esBorrador, onEditar, onIgnorar, onAObservacion }) {
    const [verCrudos, setVerCrudos] = useState(false);
    const d = fila.datos || {};
    const meta = META_ESTADO[fila.estado] || META_ESTADO.OK;
    const editar = (ruta) => (esBorrador ? () => onEditar(ruta) : null);

    return (
        <div className={`lote-fila lote-fila-${fila.estado.toLowerCase()}`}>
            <div className="lote-fila-cabecera">
                <div className="lote-fila-titulo">
                    <span className="lote-fila-nro">#{fila.nroFila}</span>
                    <strong>{d.apellido || '—'}, {d.nombre || '—'}</strong>
                    <LapizCabecera onEditar={editar('apellido')} titulo="Corregir el apellido" />
                    <LapizCabecera onEditar={editar('nombre')} titulo="Corregir el nombre" />
                    <span className="text-muted small ms-2">{d.cuil || 'sin CUIL'}</span>
                    <LapizCabecera onEditar={editar('cuil')} titulo="Corregir el CUIL" />
                    <span className={`lote-badge ${meta.clase}`}>
                        <i className={`bi ${meta.icono} me-1`}></i>{meta.etiqueta}
                    </span>
                    {d.seccion && <span className="text-muted small ms-2">{d.seccion}</span>}
                </div>
                <div className="lote-fila-acciones">
                    <button className="lote-link" onClick={() => setVerCrudos(!verCrudos)}>
                        <i className="bi bi-table me-1"></i>{verCrudos ? 'Ocultar' : 'Ver'} planilla
                    </button>
                    {esBorrador && fila.estado !== 'APLICADA' && (
                        <button className="lote-link" onClick={() => onIgnorar(fila, fila.estado !== 'IGNORADA')}>
                            <i className="bi bi-slash-circle me-1"></i>
                            {fila.estado === 'IGNORADA' ? 'Volver a incluir' : 'Saltear'}
                        </button>
                    )}
                </div>
            </div>

            {d.textoEstado && (
                <div className="lote-contactos" style={{ fontSize: '0.85rem', color: '#1f2937' }}>
                    <i className="bi bi-chat-square-text me-1"></i>{d.textoEstado}
                </div>
            )}

            <div className="lote-fila-datos">
                <Dato etiqueta="Trámite" valor={d.tipoTramite} onEditar={editar('tipoTramite')} />
                {d.subtipo && <Dato etiqueta="Subtipo" valor={d.subtipo} onEditar={editar('subtipo')} />}
                <Dato etiqueta="Liquidar"
                      valor={d.fechaLiquidar
                          ? `${formatoFechaCorta(d.fechaLiquidar)} (${ORIGEN_FECHA[d.origenFecha] || 'corregida'})`
                          : 'sin fecha'}
                      onEditar={editar('fechaLiquidar')} />
                <Dato etiqueta="Carpeta física" valor={d.carpeta} onEditar={editar('carpeta')} />
                <Dato etiqueta="Carpeta" valor={d.accionTramite === 'EXISTENTE'
                          ? `completa el trámite #${d.idTramiteExistente ?? '?'}`
                          : 'abre un trámite nuevo'}
                      onEditar={editar('accionTramite')} />
                {d.accionTramite === 'EXISTENTE' && (
                    <Dato etiqueta="Trámite" valor={d.idTramiteExistente ? `#${d.idTramiteExistente}` : 'sin elegir'}
                          onEditar={editar('idTramiteExistente')} />
                )}
                <Dato etiqueta="Teléfono" valor={d.telefonoPropio} onEditar={editar('telefonoPropio')} />
            </div>

            {(d.proximasFechas || []).length > 0 && (
                <div className="lote-contactos">
                    <i className="bi bi-calendar-plus me-1"></i>Después:{' '}
                    {d.proximasFechas.map((p, i) => (
                        <span key={i} className="lote-contacto">{formatoFechaCorta(p.fecha)} · {p.descripcion}</span>
                    ))}
                </div>
            )}
            {(d.historia || []).length > 0 && (
                <div className="lote-contactos">
                    <i className="bi bi-clock-history me-1"></i>Historia:{' '}
                    {d.historia.map((h, i) => <span key={i} className="lote-contacto">{h}</span>)}
                </div>
            )}
            {d.notaNombre && (
                <div className="lote-contactos">
                    <i className="bi bi-sticky me-1"></i>{d.notaNombre}
                </div>
            )}

            {(fila.problemas || []).length > 0 && (
                <div className="lote-problemas">
                    {fila.problemas.map((p, i) => (
                        <div key={i} className={`lote-problema lote-problema-${p.severidad.toLowerCase()}`}>
                            <i className={`bi ${p.severidad === 'ERROR' ? 'bi-x-octagon-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                            <span>{p.mensaje}</span>
                            {esBorrador && rutaEditablePara(p.campo, 'LIQUIDAR') && (
                                <button className="lote-link ms-2" onClick={() => onEditar(rutaEditablePara(p.campo, 'LIQUIDAR'))}>
                                    corregir
                                </button>
                            )}
                            {esBorrador && onAObservacion
                                && CAMPOS_EDITABLES_LIQUIDAR.find((c) => c.ruta === p.campo)?.indiceCrudo != null && (
                                <button className="lote-link ms-2" onClick={() => onAObservacion(fila, p.campo)}>
                                    mandar a observaciones
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {verCrudos && (
                <div className="lote-crudos">
                    {(fila.datosCrudos || []).map((c, i) => (
                        c ? <span key={i} className="lote-crudo"><b>{COLUMNAS_LIQUIDAR[i] || i}</b> {c}</span> : null
                    ))}
                </div>
            )}
        </div>
    );
}

/** Una fila de CARPETAS ELIMINADAS o ARCHIVO: va tal cual a la planilla histórica. */
function FilaLoteHistorica({ fila, esBorrador, onEditar, onIgnorar }) {
    const [verCrudos, setVerCrudos] = useState(false);
    const d = fila.datos || {};
    const meta = META_ESTADO[fila.estado] || META_ESTADO.OK;
    const editar = (ruta) => (esBorrador ? () => onEditar(ruta) : null);
    return (
        <div className={`lote-fila lote-fila-${fila.estado.toLowerCase()}`}>
            <div className="lote-fila-cabecera">
                <div className="lote-fila-titulo">
                    <span className="lote-fila-nro">#{fila.nroFila}</span>
                    <strong>{d.nombre || '—'}</strong>
                    <LapizCabecera onEditar={editar('nombre')} titulo="Corregir el nombre" />
                    <span className="text-muted small ms-2">{d.cuil || 'sin CUIL'}</span>
                    <LapizCabecera onEditar={editar('cuil')} titulo="Corregir el CUIL" />
                    <span className={`lote-badge ${meta.clase}`}>
                        <i className={`bi ${meta.icono} me-1`}></i>{meta.etiqueta}
                    </span>
                </div>
                <div className="lote-fila-acciones">
                    <button className="lote-link" onClick={() => setVerCrudos(!verCrudos)}>
                        <i className="bi bi-table me-1"></i>{verCrudos ? 'Ocultar' : 'Ver'} planilla
                    </button>
                    {esBorrador && fila.estado !== 'APLICADA' && (
                        <button className="lote-link" onClick={() => onIgnorar(fila, fila.estado !== 'IGNORADA')}>
                            <i className="bi bi-slash-circle me-1"></i>
                            {fila.estado === 'IGNORADA' ? 'Volver a incluir' : 'Saltear'}
                        </button>
                    )}
                </div>
            </div>
            <div className="lote-fila-datos">
                <Dato etiqueta="Trámite" valor={d.tramite} />
                <Dato etiqueta={d.origen === 'ARCHIVO' ? 'Cobró' : 'Eliminada'} valor={formatoFechaCorta(d.fecha) || d.fechaTexto} />
                {d.motivo && <Dato etiqueta="Motivo" valor={d.motivo} />}
                {d.arreglo && <Dato etiqueta="Arreglo" valor={d.arreglo} />}
                {d.seguimiento && <Dato etiqueta="Seguimiento" valor={d.seguimiento} />}
            </div>
            {(fila.problemas || []).length > 0 && (
                <div className="lote-problemas">
                    {fila.problemas.map((p, i) => (
                        <div key={i} className={`lote-problema lote-problema-${p.severidad.toLowerCase()}`}>
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <span>{p.mensaje}</span>
                            {esBorrador && p.campo === 'cuil' && (
                                <button className="lote-link ms-2" onClick={() => onEditar('cuil')}>corregir</button>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {verCrudos && (
                <div className="lote-crudos">
                    {(d.columnas || []).map((c, i) => (
                        <span key={i} className="lote-crudo"><b>{c.titulo}</b> {c.valor}</span>
                    ))}
                </div>
            )}
        </div>
    );
}

const RESULTADO_ANSES = {
    FAVORABLE: 'Favorable (queda para pasar a Favorables a mano)',
    DESFAVORABLE: 'Desfavorable',
    CITADO: 'Citado',
    EN_CURSO: 'En curso',
};

/**
 * Una fila de la solapa PARA APROBAR. Igual que LIQUIDAR: arriba lo que escribió el estudio
 * (trámite y estado), abajo lo que se va a crear. Las filas que el estudio decidió no migrar nacen
 * salteadas y dicen por qué; se pueden volver a incluir.
 */
function FilaLoteParaAprobar({ fila, esBorrador, onEditar, onIgnorar, onAObservacion, tipoLote = 'PARA_APROBAR' }) {
    const [verCrudos, setVerCrudos] = useState(false);
    const d = fila.datos || {};
    const meta = META_ESTADO[fila.estado] || META_ESTADO.OK;
    const editar = (ruta) => (esBorrador ? () => onEditar(ruta) : null);
    const esFavorables = tipoLote === 'FAVORABLES' || tipoLote === 'ALTAS';
    const columnas = esFavorables ? COLUMNAS_FAVORABLES
        : d.disposicion === 'AMPAROS' ? COLUMNAS_AMPAROS : COLUMNAS_PARA_APROBAR;

    return (
        <div className={`lote-fila lote-fila-${fila.estado.toLowerCase()}`}>
            <div className="lote-fila-cabecera">
                <div className="lote-fila-titulo">
                    <span className="lote-fila-nro">#{fila.nroFila}</span>
                    <strong>{d.apellido || '—'}, {d.nombre || '—'}</strong>
                    <LapizCabecera onEditar={editar('apellido')} titulo="Corregir el apellido" />
                    <LapizCabecera onEditar={editar('nombre')} titulo="Corregir el nombre" />
                    <span className="text-muted small ms-2">{d.cuil || 'sin CUIL'}</span>
                    <LapizCabecera onEditar={editar('cuil')} titulo="Corregir el CUIL" />
                    <span className={`lote-badge ${meta.clase}`}>
                        <i className={`bi ${meta.icono} me-1`}></i>{meta.etiqueta}
                    </span>
                    {d.seccion && <span className="text-muted small ms-2">{d.seccion}</span>}
                </div>
                <div className="lote-fila-acciones">
                    <button className="lote-link" onClick={() => setVerCrudos(!verCrudos)}>
                        <i className="bi bi-table me-1"></i>{verCrudos ? 'Ocultar' : 'Ver'} planilla
                    </button>
                    {esBorrador && fila.estado !== 'APLICADA' && (
                        <button className="lote-link" onClick={() => onIgnorar(fila, fila.estado !== 'IGNORADA')}>
                            <i className="bi bi-slash-circle me-1"></i>
                            {fila.estado === 'IGNORADA' ? 'Volver a incluir' : 'Saltear'}
                        </button>
                    )}
                </div>
            </div>

            <div className="lote-contactos" style={{ fontSize: '0.85rem', color: '#1f2937' }}>
                <i className="bi bi-chat-square-text me-1"></i>
                <strong>{d.textoTramite || '—'}</strong>{d.estadoPlanilla && <> · {d.estadoPlanilla}</>}
                {d.revisar && (
                    <span className="lote-badge lote-chip-adv ms-2" title="El tipo salió por descarte: queda con la etiqueta REVISAR MIGRACIÓN">
                        <i className="bi bi-search me-1"></i>Revisar
                    </span>
                )}
                {d.motivoCancelacion && (
                    <span className="lote-badge lote-chip-err ms-2" title={d.motivoCancelacion}>
                        <i className="bi bi-x-circle me-1"></i>Se crea cancelado
                    </span>
                )}
            </div>

            <div className="lote-fila-datos">
                <Dato etiqueta="Trámite" valor={d.tipoTramite} onEditar={editar('tipoTramite')} />
                {d.tipoTramite === 'GENERICO' && (
                    <Dato etiqueta="Título" valor={d.tituloGenerico || d.textoTramite} onEditar={editar('tituloGenerico')} />
                )}
                {!esFavorables && (
                    <Dato etiqueta="Organismo" valor={d.organismo === 'PROVINCIA' ? 'Caja Provincia' : 'ANSES'}
                          onEditar={editar('organismo')} />
                )}
                {d.subtipo && <Dato etiqueta="Subtipo" valor={d.subtipo} onEditar={editar('subtipo')} />}
                {d.tipoTramite === 'PENSION' && (
                    <>
                        <Dato etiqueta="Pensión" valor={d.tipoPension || 'DIRECTA'} onEditar={editar('tipoPension')} />
                        <Dato etiqueta="Vínculo" valor={d.vinculoBeneficiario || 'CONYUGE'} onEditar={editar('vinculoBeneficiario')} />
                    </>
                )}
                {esFavorables ? (
                    <>
                        {d.fechaAlta || tipoLote === 'ALTAS'
                            ? <Dato etiqueta={d.cobroRetenido ? 'Alta (cobro retenido)' : 'Alta (día del cobro)'} valor={formatoFechaCorta(d.fechaAlta)} onEditar={editar('fechaAlta')} />
                            : <Dato etiqueta="Favorable" valor={formatoFechaCorta(d.fechaFavorable)} onEditar={editar('fechaFavorable')} />}
                        {tipoLote === 'ALTAS' && (
                            <Dato etiqueta="Cómo pagó" valor={d.tipoPago === 'UN_SOLO_PAGO' ? 'Pagó todo'
                                : d.tipoPago === 'EN_CUOTAS' ? 'Saldo en cuotas' : 'Sin definir'} />
                        )}
                        <Dato etiqueta="Banco" valor={d.banco} onEditar={editar('banco')} />
                        <Dato etiqueta="Cobro" valor={d.idCobroExistente ? `ya existe (#${d.idCobroExistente}): se completa`
                            : d.fechaAlta ? 'nuevo, en Altas' : 'nuevo, en Favorables'} />
                    </>
                ) : (
                    <Dato etiqueta="En ANSES" valor={RESULTADO_ANSES[d.resultadoAnses] || d.resultadoAnses} />
                )}
                <Dato etiqueta="Expediente" valor={d.nroExpediente} onEditar={editar('nroExpediente')} />
                {!esFavorables && (
                    <>
                        <Dato etiqueta="Ingreso a ANSES" valor={formatoFechaCorta(d.fechaIngresoAnses)} onEditar={editar('fechaIngresoAnses')} />
                        <Dato etiqueta="Carpeta física" valor={d.carpeta} onEditar={editar('carpeta')} />
                    </>
                )}
                <Dato etiqueta="Carpeta" valor={d.accionTramite === 'EXISTENTE'
                          ? `completa el trámite #${d.idTramiteExistente ?? '?'}`
                          : 'abre un trámite nuevo'}
                      onEditar={editar('accionTramite')} />
                {d.accionTramite === 'EXISTENTE' && (
                    <Dato etiqueta="Trámite" valor={d.idTramiteExistente ? `#${d.idTramiteExistente}` : 'sin elegir'}
                          onEditar={editar('idTramiteExistente')} />
                )}
            </div>

            {((d.notas || []).length > 0 || d.amparoIniciado || d.arreglo) && (
                <div className="lote-contactos">
                    <i className="bi bi-sticky me-1"></i>
                    {d.amparoIniciado && <span className="lote-contacto">{d.amparoIniciado}</span>}
                    {d.arreglo && <span className="lote-contacto">Arreglo: {d.arreglo}</span>}
                    {(d.notas || []).map((n, i) => <span key={i} className="lote-contacto">{n}</span>)}
                </div>
            )}

            {(fila.problemas || []).length > 0 && (
                <div className="lote-problemas">
                    {fila.problemas.map((p, i) => (
                        <div key={i} className={`lote-problema lote-problema-${p.severidad.toLowerCase()}`}>
                            <i className={`bi ${p.severidad === 'ERROR' ? 'bi-x-octagon-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                            <span>{p.mensaje}</span>
                            {esBorrador && rutaEditablePara(p.campo, tipoLote) && (
                                <button className="lote-link ms-2" onClick={() => onEditar(rutaEditablePara(p.campo, tipoLote))}>
                                    corregir
                                </button>
                            )}
                            {esBorrador && onAObservacion
                                && camposDe(tipoLote).find((c) => c.ruta === p.campo)?.indiceCrudo != null && (
                                <button className="lote-link ms-2" onClick={() => onAObservacion(fila, p.campo)}>
                                    mandar a observaciones
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {verCrudos && (
                <div className="lote-crudos">
                    {(fila.datosCrudos || []).map((c, i) => (
                        c ? <span key={i} className="lote-crudo"><b>{columnas[i] || i}</b> {c}</span> : null
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Una fila de NUEVO DEUDORES EN CUOTA: el cliente, el plan, las cuotas que la planilla marca como
 * pagadas y las gestiones que van a la bitácora del cobro.
 */
function FilaLoteDeudor({ fila, esBorrador, onEditar, onIgnorar }) {
    const [verCrudos, setVerCrudos] = useState(false);
    const d = fila.datos || {};
    const meta = META_ESTADO[fila.estado] || META_ESTADO.OK;
    const editar = (ruta) => (esBorrador ? () => onEditar(ruta) : null);
    const pagadas = d.cuotasPagadas || [];
    const plan = d.cantidadCuotas
        ? `${d.cantidadCuotas} x $${Number(d.montoCuota).toLocaleString('es-AR')}`
        : (d.pagoTodo ? 'Pagó todo' : 'Sin plan');

    return (
        <div className={`lote-fila lote-fila-${fila.estado.toLowerCase()}`}>
            <div className="lote-fila-cabecera">
                <div className="lote-fila-titulo">
                    <span className="lote-fila-nro">#{fila.nroFila}</span>
                    <strong>{d.apellido || '—'}, {d.nombre || '—'}</strong>
                    <LapizCabecera onEditar={editar('apellido')} titulo="Corregir el apellido" />
                    <LapizCabecera onEditar={editar('nombre')} titulo="Corregir el nombre" />
                    <span className="text-muted small ms-2">{d.cuil || 'sin CUIL'}</span>
                    <LapizCabecera onEditar={editar('cuil')} titulo="Corregir el CUIL" />
                    <span className={`lote-badge ${meta.clase}`}>
                        <i className={`bi ${meta.icono} me-1`}></i>{meta.etiqueta}
                    </span>
                    {d.seccion && <span className="text-muted small ms-2">{d.seccion}</span>}
                </div>
                <div className="lote-fila-acciones">
                    <button className="lote-link" onClick={() => setVerCrudos(!verCrudos)}>
                        <i className="bi bi-table me-1"></i>{verCrudos ? 'Ocultar' : 'Ver'} planilla
                    </button>
                    {esBorrador && fila.estado !== 'APLICADA' && (
                        <button className="lote-link" onClick={() => onIgnorar(fila, fila.estado !== 'IGNORADA')}>
                            <i className="bi bi-slash-circle me-1"></i>
                            {fila.estado === 'IGNORADA' ? 'Volver a incluir' : 'Saltear'}
                        </button>
                    )}
                </div>
            </div>

            <div className="lote-contactos" style={{ fontSize: '0.85rem', color: '#1f2937' }}>
                <i className="bi bi-chat-square-text me-1"></i>
                <strong>{d.textoTramite || '—'}</strong>{d.adeudaTexto && <> · {d.adeudaTexto}</>}
            </div>

            <div className="lote-fila-datos">
                <Dato etiqueta="Mes de la planilla" valor={formatoFechaCorta(d.mesPlanilla)} />
                <Dato etiqueta="Plan" valor={plan} onEditar={editar('cantidadCuotas')} />
                <Dato etiqueta="Cuotas pagadas" valor={`${pagadas.length}${d.cantidadCuotas ? ` de ${d.cantidadCuotas}` : ''}`} />
                <Dato etiqueta="Gestiones" valor={String((d.gestiones || []).length)} />
                <Dato etiqueta="Trámite" valor={d.idTramiteExistente ? `${d.tipoTramite} #${d.idTramiteExistente}` : `${d.tipoTramite || '—'} (sin vincular)`} />
                <Dato etiqueta="Recordatorio" valor="apagado (se activa desde Deudores)" />
            </div>

            {pagadas.length > 0 && (
                <div className="lote-contactos">
                    <i className="bi bi-cash-coin me-1"></i>
                    {pagadas.map((p) => (
                        <span key={p.numero} className="lote-contacto">Cuota {p.numero}: {formatoFechaCorta(p.fecha)}</span>
                    ))}
                </div>
            )}
            {((d.gestiones || []).length > 0 || (d.notas || []).length > 0) && (
                <div className="lote-contactos">
                    <i className="bi bi-journal-text me-1"></i>
                    {(d.gestiones || []).map((g, i) => <span key={`g${i}`} className="lote-contacto">{g.detalle}</span>)}
                    {(d.notas || []).map((n, i) => <span key={`n${i}`} className="lote-contacto">{n}</span>)}
                </div>
            )}

            {(fila.problemas || []).length > 0 && (
                <div className="lote-problemas">
                    {fila.problemas.map((p, i) => (
                        <div key={i} className={`lote-problema lote-problema-${p.severidad.toLowerCase()}`}>
                            <i className={`bi ${p.severidad === 'ERROR' ? 'bi-x-octagon-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                            <span>{p.mensaje}</span>
                            {esBorrador && rutaEditablePara(p.campo, 'DEUDORES') && (
                                <button className="lote-link ms-2" onClick={() => onEditar(rutaEditablePara(p.campo, 'DEUDORES'))}>
                                    corregir
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {verCrudos && (
                <div className="lote-crudos">
                    {(fila.datosCrudos || []).map((c, i) => (
                        c ? <span key={i} className="lote-crudo"><b>{COLUMNAS_DEUDORES[i] || i}</b> {c}</span> : null
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Una fila de PARA CARGAR JAUREGUI: el cliente, el reajuste (el que ya está en el cálculo o uno
 * nuevo) y la carpeta: bloque, fechas, quién la carga y resultado.
 */
function FilaLoteJauregui({ fila, esBorrador, onEditar, onIgnorar }) {
    const [verCrudos, setVerCrudos] = useState(false);
    const d = fila.datos || {};
    const meta = META_ESTADO[fila.estado] || META_ESTADO.OK;
    const editar = (ruta) => (esBorrador ? () => onEditar(ruta) : null);

    return (
        <div className={`lote-fila lote-fila-${fila.estado.toLowerCase()}`}>
            <div className="lote-fila-cabecera">
                <div className="lote-fila-titulo">
                    <span className="lote-fila-nro">#{fila.nroFila}</span>
                    <strong>{d.apellido || '—'}, {d.nombre || '—'}</strong>
                    <LapizCabecera onEditar={editar('apellido')} titulo="Corregir el apellido" />
                    <LapizCabecera onEditar={editar('nombre')} titulo="Corregir el nombre" />
                    <span className="text-muted small ms-2">{d.cuil || 'sin CUIL'}</span>
                    <LapizCabecera onEditar={editar('cuil')} titulo="Corregir el CUIL" />
                    <span className={`lote-badge ${meta.clase}`}>
                        <i className={`bi ${meta.icono} me-1`}></i>{meta.etiqueta}
                    </span>
                </div>
                <div className="lote-fila-acciones">
                    <button className="lote-link" onClick={() => setVerCrudos(!verCrudos)}>
                        <i className="bi bi-table me-1"></i>{verCrudos ? 'Ocultar' : 'Ver'} planilla
                    </button>
                    {esBorrador && fila.estado !== 'APLICADA' && (
                        <button className="lote-link" onClick={() => onIgnorar(fila, fila.estado !== 'IGNORADA')}>
                            <i className="bi bi-slash-circle me-1"></i>
                            {fila.estado === 'IGNORADA' ? 'Volver a incluir' : 'Saltear'}
                        </button>
                    )}
                </div>
            </div>

            <div className="lote-contactos" style={{ fontSize: '0.85rem', color: '#1f2937' }}>
                <i className="bi bi-chat-square-text me-1"></i>
                <strong>{d.textoTramite || '—'}</strong>
            </div>

            <div className="lote-fila-datos">
                <Dato etiqueta="Bloque" valor={ETIQUETA_BLOQUE_JAUREGUI[d.bloque] || d.bloque} />
                <Dato etiqueta="Reajuste" valor={d.accionTramite === 'EXISTENTE'
                          ? `usa el #${d.idTramiteExistente ?? '?'}` : 'abre uno nuevo (migrado)'}
                      onEditar={editar('accionTramite')} />
                {d.fechaLlamado
                    ? <Dato etiqueta="Llamado" valor={formatoFechaCorta(d.fechaLlamado)} onEditar={editar('fechaLlamado')} />
                    : <Dato etiqueta="Pase" valor={formatoFechaCorta(d.fechaPase)} onEditar={editar('fechaPase')} />}
                <Dato etiqueta="Carga" valor={d.quienCarga || '—'} />
                <Dato etiqueta="Resultado" valor={d.resultado || 'pendiente'} />
            </div>

            {d.observacionCarpeta && (
                <div className="lote-contactos">
                    <i className="bi bi-sticky me-1"></i>
                    <span className="lote-contacto">{d.observacionCarpeta}</span>
                </div>
            )}

            {(fila.problemas || []).length > 0 && (
                <div className="lote-problemas">
                    {fila.problemas.map((p, i) => (
                        <div key={i} className={`lote-problema lote-problema-${p.severidad.toLowerCase()}`}>
                            <i className={`bi ${p.severidad === 'ERROR' ? 'bi-x-octagon-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                            <span>{p.mensaje}</span>
                            {esBorrador && rutaEditablePara(p.campo, 'JAUREGUI') && (
                                <button className="lote-link ms-2" onClick={() => onEditar(rutaEditablePara(p.campo, 'JAUREGUI'))}>
                                    corregir
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {verCrudos && (
                <div className="lote-crudos">
                    {(fila.datosCrudos || []).map((c, i) => (
                        c ? <span key={i} className="lote-crudo"><b>{COLUMNAS_JAUREGUI[i] || i}</b> {c}</span> : null
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Elegir contra qué cliente del estudio se vincula la referencia.
 *
 * El aviso pedía "confirmar contra qué cliente se vincula" y el modal era un campo de texto: no
 * había forma de saber el CUIL de memoria. Se busca por nombre o apellido y se guarda el CUIL,
 * que es lo que el backend necesita para enlazar.
 */
function BuscadorCliente({ valor, onElegir }) {
    const [criterio, setCriterio] = useState('');
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando] = useState(false);

    useEffect(() => {
        const texto = criterio.trim();
        if (texto.length < 3) {
            setResultados([]);
            return undefined;
        }
        // Se espera a que deje de tipear: sin esto sale una consulta por tecla.
        const id = setTimeout(async () => {
            setBuscando(true);
            try {
                setResultados(await buscarClientes(texto));
            } catch {
                setResultados([]);
            } finally {
                setBuscando(false);
            }
        }, 350);
        return () => clearTimeout(id);
    }, [criterio]);

    return (
        <>
            {valor && (
                <div className="alert alert-success border-0 rounded-3 py-2 px-3 mb-2 small">
                    Vinculado al CUIL <strong>{valor}</strong>
                    <button className="lote-link ms-2" onClick={() => onElegir('')}>quitar</button>
                </div>
            )}
            <Form.Control
                autoFocus
                placeholder="Buscar por nombre o apellido..."
                value={criterio}
                onChange={(e) => setCriterio(e.target.value)}
            />
            <div className="lote-buscador-resultados mt-2">
                {buscando && <div className="text-muted small">Buscando...</div>}
                {!buscando && criterio.trim().length >= 3 && resultados.length === 0 && (
                    <div className="text-muted small">Ningún cliente coincide.</div>
                )}
                {resultados.map((c) => (
                    <button
                        key={c.cuil}
                        className={`lote-buscador-item ${valor === c.cuil ? 'activo' : ''}`}
                        onClick={() => onElegir(c.cuil)}
                    >
                        <strong>{apellidoNombre(c.apellido, c.nombre)}</strong>
                        <span className="text-muted ms-2">{c.cuil}</span>
                    </button>
                ))}
            </div>
        </>
    );
}

/** Lápiz de corrección para los datos del encabezado (identidad de la fila). */
function LapizCabecera({ onEditar, titulo }) {
    if (!onEditar) return null;
    return (
        <button className="lote-dato-editar" onClick={onEditar} title={titulo}>
            <i className="bi bi-pencil"></i>
        </button>
    );
}

function Dato({ etiqueta, valor, onEditar }) {
    return (
        <span className="lote-dato">
            {etiqueta && <span className="lote-dato-et">{etiqueta}</span>}
            <span className="lote-dato-v">{valor || '—'}</span>
            {onEditar && (
                <button className="lote-dato-editar" onClick={onEditar} title="Corregir">
                    <i className="bi bi-pencil"></i>
                </button>
            )}
        </span>
    );
}

export default ImportacionLotePage;
